import { FC, useCallback, useState } from 'react'
import { Box, Button, HStack, useCheckbox, useCheckboxGroup, UseCheckboxGroupReturn, VStack } from '@chakra-ui/react'
import theme from '../../styles'
import { useTranslation } from 'react-i18next'
import { Choice } from '../../state/interfaces'

export interface SmallPillCheckboxesProps {
  choices: Array<Choice>
  onChange?: (newValues: string[]) => void
  onConfirm?: (values: string[]) => void
}

interface SmallPillCheckboxProps extends ReturnType<UseCheckboxGroupReturn['getCheckboxProps']> {
  choice: Choice
  selectedChoices: string[]
}

const SmallPillCheckbox: FC<SmallPillCheckboxProps> = ({ choice: { label, value }, selectedChoices, ...props }) => {
  const { getInputProps, getCheckboxProps } = useCheckbox({
    ...props,
    isChecked: selectedChoices.some((choice) => choice === value),
  })

  const input = getInputProps()
  const checkbox = getCheckboxProps()

  return (
    <Box as="label">
      <input {...input} />
      <Box {...checkbox} role={'checkbox'} layerStyle={theme.LayerStyles.SMALL_PILL}>
        {label}
      </Box>
    </Box>
  )
}

export const SmallPillCheckboxes: FC<SmallPillCheckboxesProps> = ({ choices, onConfirm, onChange }) => {
  const { t } = useTranslation()

  const [selectedChoices, setSelectedChoices] = useState<string[]>([])

  const onAnswersChange = useCallback(
    (values: string[]) => {
      setSelectedChoices(values)
      onChange && onChange(values)
    },
    [onChange, setSelectedChoices],
  )

  const { getCheckboxProps, setValue } = useCheckboxGroup({
    onChange: onAnswersChange,
  })

  const confirmChoices = useCallback(() => {
    onConfirm && onConfirm(selectedChoices)
  }, [onConfirm, selectedChoices])

  const chooseAll = useCallback(() => {
    const allChoices = choices.filter((choice) => choice.value !== 'all').map((choice) => choice.value)

    setSelectedChoices(allChoices)
    setValue(allChoices)
    onConfirm && onConfirm(allChoices)
  }, [choices, onConfirm, setValue])

  const chooseAllChoice = choices.find((choice) => choice.value === 'all')

  return (
    <VStack width="full" marginTop={5}>
      <HStack width="full" flexWrap="wrap" justifyContent="center">
        {chooseAllChoice && (
          <Box as="button" onClick={chooseAll} layerStyle={theme.LayerStyles.SMALL_PILL}>
            {chooseAllChoice.label}
          </Box>
        )}

        {choices
          .filter((choice) => choice.value !== 'all')
          .map((choice) => {
            const checkbox = getCheckboxProps({ value: choice.value })

            return (
              <SmallPillCheckbox key={choice.value} choice={choice} {...checkbox} selectedChoices={selectedChoices} />
            )
          })}
      </HStack>
      <Button
        onClick={confirmChoices}
        disabled={selectedChoices.length === 0}
        variant="solid"
        color="purple"
        backgroundColor="purpleAlpha"
        fontSize="xs"
        fontWeight="bold"
        _focus={{
          borderColor: 'brand',
          boxShadow: '0 0 0 2px #5bce4c',
        }}
      >
        {t('common_confirm')}
      </Button>
    </VStack>
  )
}
